import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ExternalLinkIcon, X } from 'lucide-react';
import { CaseChallenge } from './CaseChallenge';
import { CaseExperiment } from './CaseExperiment';
import { CaseConversation } from './CaseConversation';
import { CaseResults } from './CaseResults';
import { CaseInsight } from './CaseInsight';
import { CaseOpportunity } from './CaseOpportunity';
import { BOEHRINGER_SNAK_URL } from '../../data/boehringerSnak';
const CASE_SLIDES = [CaseChallenge, CaseExperiment, CaseConversation, CaseResults, CaseInsight, CaseOpportunity];
interface NordicCaseOverlayProps {
  open: boolean;
  onClose: () => void;
}

/** Full-screen mini-presentation: the BI Denmark case, told for BI Australia. */
export function NordicCaseOverlay({
  open,
  onClose
}: NordicCaseOverlayProps) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) {
      setIndex(0);
      setDirection(1);
      closeRef.current?.focus();
    }
  }, [open]);
  const goTo = (next: number) => {
    if (next < 0 || next >= CASE_SLIDES.length) return;
    setDirection(next > index ? 1 : -1);
    setIndex(next);
  };
  useEffect(() => {
    if (!open) return;
    // Capture phase so the main deck doesn't also react to these keys.
    const handleKeyDown = (e: KeyboardEvent) => {
      const forward = ['ArrowRight', ' ', 'PageDown'];
      const back = ['ArrowLeft', 'PageUp'];
      if (e.key === 'Escape') onClose();else if (forward.includes(e.key)) goTo(index + 1);else if (back.includes(e.key)) goTo(index - 1);else if (e.key === 'Home') goTo(0);else if (e.key === 'End') goTo(CASE_SLIDES.length - 1);else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [open, index, onClose]);
  const CurrentSlide = CASE_SLIDES[index];
  return createPortal(<AnimatePresence data-id="element-170" data-mp="24wfzmmb6gfmi">
      {open && <motion.div role="dialog" aria-modal="true" aria-label="Nordic case: reaching HCPs the field force doesn't reach" initial={{
      opacity: 0,
      scale: 0.98
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.98
    }} transition={{
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }} className="fixed inset-0 z-[90] overflow-hidden bg-[var(--bg)] hide-on-print" data-id="element-171" data-mp="24vechjgw02of">
          <AnimatePresence mode="wait" custom={direction} data-id="element-172" data-mp="1r512zcockfwz">
            <motion.div key={index} initial={{
          opacity: 0,
          x: direction > 0 ? 40 : -40
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: direction > 0 ? -40 : 40
        }} transition={{
          duration: 0.35,
          ease: [0.4, 0, 0.2, 1]
        }} className="absolute inset-0" data-id="element-173" data-mp="1r5j7sxp2s2gi">
              <CurrentSlide data-id="element-174" data-mp="tjq6o85m2xsh" />
            </motion.div>
          </AnimatePresence>

          <div className="absolute left-0 right-0 top-0 h-1 bg-black/5" data-id="element-175" data-mp="17j7x0ryk2j3y">
            <motion.div className="h-full bg-[var(--brand)]" animate={{
          width: `${(index + 1) / CASE_SLIDES.length * 100}%`
        }} transition={{
          duration: 0.35
        }} data-id="element-176" data-mp="17kj1ktztruw4" />
          </div>

          <div className="absolute left-3 right-3 top-3 flex items-center justify-between gap-3" data-id="element-177" data-mp="1cikj3ahlrm5h">
            <div className="flex items-center gap-2 rounded-full border border-black/10 bg-white/90 px-3.5 py-1.5 shadow-sm backdrop-blur" data-id="element-178" data-mp="1q9h9qvryxt9w">
              <span className="h-2 w-2 rounded-full bg-[var(--brand)]" aria-hidden="true" data-id="element-179" data-mp="1m9f6wjahytt1" />
              <span className="text-xs font-semibold text-[var(--fg)]" data-id="element-180" data-mp="208ybx380hp14">
                Nordic case · BI Denmark → BI Australia
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-181" data-mp="2fb6iinaegm7j">
              <a href={BOEHRINGER_SNAK_URL} target="_blank" rel="noopener noreferrer" className="flex h-8 items-center gap-1.5 rounded-full border border-black/10 bg-white/90 px-3.5 text-xs font-semibold text-[var(--brand)] shadow-sm backdrop-blur transition-colors hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]" data-id="element-182" data-mp="2faevw1ys5yte">
                Live Snak
                <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-183" data-mp="20cw0whviepdi" />
              </a>
              <button ref={closeRef} onClick={onClose} aria-label="Close Nordic case and return to the deck" className="flex h-8 items-center gap-1.5 rounded-full bg-[var(--fg)] px-3.5 text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-184" data-mp="25c7hnqvvluv3">
                <X className="h-3.5 w-3.5" aria-hidden="true" data-id="element-185" data-mp="1a2voe4tlzwii" />
                Close
              </button>
            </div>
          </div>

          <div className="absolute bottom-3 right-3 flex items-center gap-2" data-id="element-186" data-mp="10dc1mo2rziid">
            <button onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Previous case slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:cursor-not-allowed disabled:opacity-30" data-id="element-187" data-mp="ver08lbsessc">
              <ChevronLeft className="h-4 w-4" data-id="element-188" data-mp="cfwlvdg8jucp" />
            </button>
            <div className="flex items-baseline gap-1 rounded-full bg-[var(--brand)] px-3.5 py-1.5 shadow-md" data-id="element-189" data-mp="chptow2l4kma">
              <span className="text-sm font-bold tabular-nums text-white" data-id="element-190" data-mp="2bqwl2ym1lkjf">{index + 1}</span>
              <span className="text-xs font-medium text-[var(--brand-lighter)]" data-id="element-191" data-mp="26uphvdl16n56">/ {CASE_SLIDES.length}</span>
            </div>
            {index < CASE_SLIDES.length - 1 ? <button onClick={() => goTo(index + 1)} aria-label="Next case slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white" data-id="element-192" data-mp="9w8lyokizmem">
                <ChevronRight className="h-4 w-4" data-id="element-193" data-mp="nt5b8lkzwvte" />
              </button> : <button onClick={onClose} className="flex h-8 items-center rounded-full bg-[var(--fg)] px-3.5 text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-90" data-id="element-194" data-mp="stjku828wdtj">
                Back to deck
              </button>}
          </div>
        </motion.div>}
    </AnimatePresence>, document.body);
}